import {
  Box,
  Button,
  Flex,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { OnboardingCheckIcon } from '../../../theme/icons/OnboardingCheckIcon';

export type StepModalConfig = {
  stepNumber: number;
  completedLabel: string;
  tagline: string;
  encouragement: string;
  upNextDescription: string;
  ctaLabel: string;
  eta: string;
};

const TOTAL_STEPS = 4;

const OnboardingStepCompletionModal = ({
  isOpen,
  onClose,
  onCta,
  config,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCta: () => void;
  config: StepModalConfig;
}) => {
  const pct = (config.stepNumber / TOTAL_STEPS) * 100;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size="md"
      closeOnOverlayClick={false}
    >
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
      <ModalContent
        borderRadius="20px"
        overflow="hidden"
        bg="white"
        boxShadow="0 24px 60px -16px rgba(17,22,34,0.55), 0 6px 18px -4px rgba(232,93,58,0.35)"
      >
        <ModalBody p={0}>
          {/* ── Orange header section ─────────────────────────────────── */}
          <Box
            background="onboardingBgGradient"
            color="white"
            px={8}
            pt={8}
            pb={7}
            textAlign="center"
          >
            <Flex justify="center" mb={4}>
              <Flex
                align="center"
                justify="center"
                boxSize="40px"
                borderRadius="full"
                border="1.5px solid"
                borderColor="whiteAlpha.800"
                color="white"
              >
                <OnboardingCheckIcon boxSize="18px" />
              </Flex>
            </Flex>

            <Text
              fontFamily="heading"
              fontSize="11px"
              letterSpacing="2px"
              textTransform="uppercase"
              fontWeight="700"
              color="white"
              mb={3}
            >
              Step {config.stepNumber} of {TOTAL_STEPS} ·{' '}
              {config.completedLabel}
            </Text>

            <Box
              mx="auto"
              maxW="180px"
              h="3px"
              borderRadius="full"
              bg="whiteAlpha.400"
              mb={5}
              overflow="hidden"
            >
              <Box
                h="100%"
                w={`${pct}%`}
                bg="white"
                borderRadius="full"
                transition="width 0.45s cubic-bezier(.4,.7,.2,1)"
              />
            </Box>

            <Text
              fontFamily="heading"
              fontSize="24px"
              fontWeight="800"
              letterSpacing="-0.3px"
              lineHeight="1.2"
              color="white"
            >
              {config.tagline}
            </Text>
          </Box>

          {/* ── White body section ────────────────────────────────────── */}
          <Box bg="white" color="inkBlue" px={10} py={8} textAlign="center">
            <Text
              fontFamily="heading"
              fontSize="20px"
              fontWeight="700"
              lineHeight="1.25"
              color="inkBlue"
              mb={3}
            >
              {config.encouragement}
            </Text>

            <Text
              fontFamily="body"
              fontSize="14px"
              lineHeight="1.5"
              color="inkBlue"
              opacity={0.85}
              mb={6}
              whiteSpace="pre-line"
            >
              {config.upNextDescription}
            </Text>

            <Text
              fontFamily="body"
              fontSize="13px"
              lineHeight="1.5"
              color="inkBlue"
              opacity={0.85}
              mb={6}
            >
              {config.eta}
            </Text>

            <Button onClick={onCta} variant="gradient" w="full" mb={4}>
              {config.ctaLabel}
            </Button>

            <Flex justify="center">
              <Link
                onClick={onClose}
                fontFamily="heading"
                fontSize="13px"
                fontWeight="600"
                color="inkBlue"
                textDecoration="underline"
                _hover={{ color: 'onboardingAccent' }}
                cursor="pointer"
              >
                I'll do this later
              </Link>
            </Flex>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default OnboardingStepCompletionModal;
